import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useAdminStatus } from '../hooks/useAdminStatus';
import { useNotify } from '../context/NotificationContext';
import { shortenAddress } from '../utils/format';
import { Spinner, PageLoader } from '../components/common/Loader';
import NetworkGuard from '../components/common/NetworkGuard';
import ContractHealthPanel from '../components/admin/ContractHealthPanel';
import SettingsPanel from '../components/admin/SettingsPanel';
import TiersPanel from '../components/admin/TiersPanel';
import LevelConfigPanel from '../components/admin/LevelConfigPanel';
import MachineDisplayPanel from '../components/admin/MachineDisplayPanel';
import WalletsPanel from '../components/admin/WalletsPanel';
import BlacklistPanel from '../components/admin/BlacklistPanel';
import PlatformStatsPanel from '../components/admin/PlatformStatsPanel';
import PoolsPanel from '../components/admin/PoolsPanel';
import DangerZone from '../components/admin/DangerZone';
import '../components/admin/Admin.css';

const TABS = [
  { key: 'contracts', label: 'Contracts' },
  { key: 'stats', label: 'Stats & Lookup' },
  { key: 'settings', label: 'Settings' },
  { key: 'tiers', label: 'Package Tiers' },
  { key: 'levels', label: 'Level Rewards' },
  { key: 'pools', label: 'Pool Rewards' },
  { key: 'display', label: 'Machine Display' },
  { key: 'wallets', label: 'Wallets & Shares' },
  { key: 'blacklist', label: 'Blacklist' },
  { key: 'danger', label: 'Danger Zone' },
];

export default function AdminPage() {
  const [tab, setTab] = useState('contracts');
  const { isConnected, account, connect, connecting } = useWallet();
  const { isOwner, isPrivileged, owner, admin, loading, verified } = useAdminStatus();
  const notify = useNotify();
  const [notifiedOnce, setNotifiedOnce] = useState(false);

  // Notify non-privileged users ONLY after check has completed successfully.
  useEffect(() => {
    if (isConnected && verified === true && isPrivileged === false && !notifiedOnce) {
      setNotifiedOnce(true);
      notify.error('This section is restricted.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, verified, isPrivileged, notifiedOnce]);

  // Reset notifier when account changes so the toast fires once per switch
  useEffect(() => {
    setNotifiedOnce(false);
  }, [account]);

  // Not connected → connect prompt
  if (!isConnected) {
    return (
      <div className="container admin-page">
        <div className="empty-state">
          <div className="icon">🔒</div>
          <h3 style={{ marginBottom: 8 }}>Connect Your Wallet</h3>
          <button className="btn btn-primary btn-lg mt-4" onClick={connect} disabled={connecting}>
            {connecting ? <><Spinner /> Connecting</> : 'Connect Wallet'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <NetworkGuard>
      {/*
        Order matters:
        1. If we're still loading OR haven't finished the on-chain check, show a neutral loader.
           (Never redirect based on !isPrivileged before verified — that kicked out owners/admins on first render.)
        2. Once verified: privileged → show panel; not privileged → redirect silently.
      */}
      {loading || !verified ? (
        <div className="container admin-page">
          <PageLoader label="VERIFYING" sub="Checking permissions..." />
        </div>
      ) : !isPrivileged ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <div className="container admin-page">
          <div className="admin-header">
            <div className="admin-title">
              <h1>Admin <span className="text-gradient">Panel</span></h1>
              <span className="badge-role">
                {isOwner ? '👑 OWNER' : '🛡 ADMIN'}
              </span>
            </div>
          </div>

          <div className="admin-role-info">
            <div className="item">Owner: <strong>{owner ? shortenAddress(owner) : '—'}</strong></div>
            <div className="item">Admin: <strong>{admin ? shortenAddress(admin) : '—'}</strong></div>
            <div className="item">You: <strong>{shortenAddress(account)}</strong></div>
          </div>

          <div className="admin-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                className={`admin-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'contracts' && <ContractHealthPanel />}
          {tab === 'stats'     && <PlatformStatsPanel />}
          {tab === 'settings'  && <SettingsPanel />}
          {tab === 'tiers'     && <TiersPanel />}
          {tab === 'levels'    && <LevelConfigPanel />}
          {tab === 'pools'     && <PoolsPanel />}
          {tab === 'display'   && <MachineDisplayPanel />}
          {tab === 'wallets'   && <WalletsPanel />}
          {tab === 'blacklist' && <BlacklistPanel />}
          {tab === 'danger'    && <DangerZone />}
        </div>
      )}
    </NetworkGuard>
  );
}
